import { Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { GATEWAY_URL } from '../../Helper';
import { useApi } from '../../hooks/useApi';
import { DataGrid } from '@mui/x-data-grid';
import { IconButton } from '@mui/material';
import { Add, DeleteForever, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useErrorDialog } from '../../providers/ErrorDialogProvider';

function AddEditDialog({ selectedCompany, postCompany, setSelectedCompany }) {
  const [companyName, setCompanyName] = useState("");
  const [maxAccounts, setMaxAccounts] = useState("");
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    if (selectedCompany) {
      setCompanyName(selectedCompany.companyName || "");
      setMaxAccounts(selectedCompany.maxAccounts || "");
      setIsNew(selectedCompany.companyName == null);
    }
  }, [selectedCompany]);

  return (
    <Dialog open={Boolean(selectedCompany)} onClose={() => setSelectedCompany(null)}>
      <DialogTitle>{!isNew ? "Team bearbeiten" : "Team hinzufügen"}</DialogTitle>
      <DialogContent style={{ width: "450px" }}>
        <TextField
          label="Team Name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          style={{ marginTop: "10px", width: "100%" }}
        />
        <TextField
          label="Anzahl der Zugänge"
          type="number"
          value={maxAccounts}
          onChange={(e) => setMaxAccounts(e.target.value)}
          style={{ marginTop: "10px", width: "100%" }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setSelectedCompany(null)}>
          ABBRECHEN
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            postCompany(companyName, Number(maxAccounts));
            setSelectedCompany(null);
          }}
          disabled={!companyName || !maxAccounts} // Prevents empty submissions
        >
          {!isNew ? "BEARBEITEN" : "HINZUFÜGEN"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CompanyView() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null); 

  const { showError } = useErrorDialog();

  const api = useApi();

  function getCompanies() {
    api.customRequest(GATEWAY_URL + '/getCompanies', {method: "GET"}, async (resp) => {
      let items = await resp?.json();
      if (resp.ok && items) {
        setItems(items)
      }
    });
  }

  function postCompany(companyName, maxAccounts) {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify({
        ...selectedCompany,
        companyName,
        maxAccounts
      }),
      redirect: "follow"
    };
    api.customRequest(GATEWAY_URL + '/addCompany', requestOptions, async (resp) => {
      getCompanies()
      if(!resp.ok) {
        let error = await resp.json()
        showError(error?.message ?? "Unknown")
      }
    });
  }

  function deleteCompany(companyToDelete) {
    const requestOptions = {
      method: "DELETE",
      redirect: "follow"
    };
    api.customRequest(GATEWAY_URL + '/deleteCompany/' + companyToDelete?._id, requestOptions, async (resp) => {
      getCompanies();
      setCompanyToDelete(null);
      if(!resp.ok) {
        let error = await resp.json();
        showError(error?.message ?? "Unknown");
      }
    });
  }

  const columns = [
    { 
      field: 'companyName',
      headerName: 'Team Name',
      flex: 1
    },
    { 
      field: 'activeAccounts',
      headerName: 'Aktive Zugänge',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => `${params.row.activeAccounts ?? 0}/${params.row.maxAccounts ?? 0}`
    },
    { 
      field: 'actions', 
      headerName: 'Funktionen', 
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <>
          <Tooltip title="Team bearbeiten">
            <IconButton aria-label="edit" onClick={(e) => {
              setSelectedCompany(params.row);
              e.stopPropagation();
            }}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Team löschen">
            <IconButton aria-label="delete" onClick={(e) => {
              setCompanyToDelete(params.row);
              e.stopPropagation();
            }}>
              <DeleteForever />
            </IconButton>
          </Tooltip>
        </>
      ) 
    }
  ];

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="content">
      <Container style={{display: "flex", flexDirection: "column", gap: 16}}>
        <div style={{display: "flex", alignItems: "center", marginTop: '50px', justifyContent: "space-between"}}>
          <Typography fontSize={30}>TEAMS</Typography>
          <Tooltip title="Team hinzufügen">
            <IconButton onClick={() => setSelectedCompany({})}>
              <Add/>
            </IconButton>
          </Tooltip>
        </div>
        
        {/* Render list view with all Codes */}
        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={items.map((item, index) => ({ id: index, ...item }))}
            rowSelection={true}
            columns={columns}
            pageSize={5}
            disableSelectionOnClick
            onRowClick={(item) => {
              navigate(`/teams/${item.row._id}`, { state: { selectedCompany: item.row } })
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                cursor: 'pointer', // Add pointer cursor on hover
              }
            }}
          />
        </div>

        <AddEditDialog
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          postCompany={postCompany}
        />

        <Dialog open={companyToDelete != null}>
          <DialogTitle>Team löschen?</DialogTitle>
          <DialogContent>
            <p>
              Möchten Sie "{companyToDelete?.companyName}" wirklich löschen?
            </p>
          </DialogContent>
          <DialogActions>
            <Button type="button" variant='outlined' onClick={() => {
                setCompanyToDelete(null);
              }}
            >
              NEIN
            </Button>
            <Button type="button" variant='contained' onClick={() => {
                deleteCompany(companyToDelete);
                setCompanyToDelete(null);
              }}
            >
              JA
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </div>
  );
}

export default CompanyView;
